/**
 * API functions for custom assessment domain management.
 * Used by EM and SME portals to create, edit, and manage assessment domains.
 */
import { authFetch } from './authFetch';

// ── Types ──────────────────────────────────────────────────────

export interface CustomDomainQuestion {
  id: string;
  question_key: string;
  question_text: string;
  scale_type: string;
  options?: any[];
  sort_order: number;
  section?: string;
}

export interface CustomDomainAsset {
  id: string;
  filename: string;
  file_size_bytes?: number;
  description?: string;
  uploaded_by: string;
  uploaded_by_name?: string;
  created_at?: string;
}

export interface CustomDomain {
  id: string;
  name: string;
  domain_id: string;
  category: string;
  description?: string;
  agent_key?: string;
  status: 'draft' | 'active' | 'archived';
  is_builtin?: boolean;
  question_count?: number;
  asset_count?: number;
  created_by: string;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
  questions?: CustomDomainQuestion[];
  assets?: CustomDomainAsset[];
}

// ── Domain CRUD ────────────────────────────────────────────────

export async function fetchCustomDomains(params?: {
  status?: string;
  category?: string;
}): Promise<CustomDomain[]> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.category) qs.set('category', params.category);
  const url = `/api/custom-domains${qs.toString() ? `?${qs}` : ''}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`Failed to fetch custom domains: ${res.status}`);
  const data = await res.json();
  return data.domains;
}

export async function fetchCustomDomain(domainId: string): Promise<CustomDomain> {
  const res = await authFetch(`/api/custom-domains/${domainId}`);
  if (!res.ok) throw new Error(`Failed to fetch domain: ${res.status}`);
  const data = await res.json();
  return data.domain;
}

export async function createCustomDomain(payload: {
  name: string;
  category: string;
  description?: string;
  agent_key?: string;
}): Promise<CustomDomain> {
  const res = await authFetch('/api/custom-domains', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create domain: ${res.status}`);
  }
  const data = await res.json();
  return data.domain;
}

export async function updateCustomDomain(
  domainId: string,
  payload: { name?: string; category?: string; description?: string; status?: string; agent_key?: string },
): Promise<void> {
  const res = await authFetch(`/api/custom-domains/${domainId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update domain: ${res.status}`);
  }
}

export async function deleteCustomDomain(domainId: string): Promise<void> {
  const res = await authFetch(`/api/custom-domains/${domainId}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete domain: ${res.status}`);
  }
}

// ── Questions ──────────────────────────────────────────────────

export async function addQuestion(
  domainId: string,
  payload: { question_key: string; question_text: string; scale_type?: string; options?: any[] },
): Promise<CustomDomainQuestion> {
  const res = await authFetch(`/api/custom-domains/${domainId}/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to add question: ${res.status}`);
  }
  const data = await res.json();
  return data.question;
}

export async function bulkAddQuestions(
  domainId: string,
  questions: { question_key: string; question_text: string; scale_type?: string; section?: string }[],
): Promise<{ added: number; skipped: number; errors: string[] }> {
  const res = await authFetch(`/api/custom-domains/${domainId}/questions/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questions }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Bulk upload failed: ${res.status}`);
  }
  return res.json();
}

export async function updateQuestion(
  domainId: string,
  questionId: string,
  payload: { question_text?: string; scale_type?: string; options?: any[]; sort_order?: number },
): Promise<void> {
  const res = await authFetch(`/api/custom-domains/${domainId}/questions/${questionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update question: ${res.status}`);
  }
}

export async function deleteQuestion(domainId: string, questionId: string): Promise<void> {
  const res = await authFetch(`/api/custom-domains/${domainId}/questions/${questionId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete question: ${res.status}`);
  }
}

// ── Assets ─────────────────────────────────────────────────────

export async function uploadDomainAsset(
  domainId: string,
  file: File,
  description?: string,
): Promise<CustomDomainAsset> {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);

  const res = await authFetch(`/api/custom-domains/${domainId}/assets`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to upload asset: ${res.status}`);
  }
  const data = await res.json();
  return data.asset;
}

export async function deleteDomainAsset(domainId: string, assetId: string): Promise<void> {
  const res = await authFetch(`/api/custom-domains/${domainId}/assets/${assetId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete asset: ${res.status}`);
  }
}

export async function updateDomainAsset(
  domainId: string,
  assetId: string,
  file?: File,
  description?: string,
): Promise<CustomDomainAsset> {
  const formData = new FormData();
  if (file) formData.append('file', file);
  if (description !== undefined) formData.append('description', description);

  const res = await authFetch(`/api/custom-domains/${domainId}/assets/${assetId}`, {
    method: 'PUT',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update asset: ${res.status}`);
  }
  const data = await res.json();
  return data.asset;
}

// ── Inline asset content (JSON/TXT) ────────────────────────────

export async function getAssetContent(
  domainId: string,
  assetId: string,
): Promise<{ content: string; filename: string }> {
  const res = await authFetch(`/api/custom-domains/${domainId}/assets/${assetId}/content`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to read asset content: ${res.status}`);
  }
  return res.json();
}

export async function saveAssetContent(
  domainId: string,
  assetId: string,
  content: string,
): Promise<{ message: string }> {
  const res = await authFetch(`/api/custom-domains/${domainId}/assets/${assetId}/content`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to save asset content: ${res.status}`);
  }
  return res.json();
}

// ── AI Question Generation ─────────────────────────────────────

export interface GeneratedQuestion {
  question_text: string;
  scale_type: string;
  sort_order: number;
  section?: string;
}

export interface GenerateQuestionsResult {
  success: boolean;
  generated_questions: GeneratedQuestion[];
  count: number;
  agent_used: string;
  model_deployment: string;
  error?: string;
}

export async function generateQuestions(
  domainId: string,
  params?: { count?: number; focus_areas?: string[]; scale_type?: string },
): Promise<GenerateQuestionsResult> {
  const res = await authFetch(`/api/custom-domains/${domainId}/generate-questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params || {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Generation failed: ${res.status}`);
  return data;
}

// ── Asset Indexing (Azure AI Search) ───────────────────────────

export interface IndexAssetsResult {
  success: boolean;
  message: string;
  indexed_assets: number;
  document_chunks: number;
  skipped: { id: string; filename: string; reason: string }[];
  error?: string;
}

export async function indexDomainAssets(
  domainId: string,
  assetIds?: string[],
): Promise<IndexAssetsResult> {
  const res = await authFetch(`/api/custom-domains/${domainId}/assets/index`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(assetIds ? { asset_ids: assetIds } : {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Indexing failed: ${res.status}`);
  return data;
}

// ── Document Cracking Indexer Pipeline ─────────────────────────

export async function setupIndexerPipeline(intervalMinutes?: number): Promise<{
  success: boolean; data_source: string; skillset: string; indexer: string;
}> {
  const res = await authFetch('/api/custom-domains/indexer/setup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(intervalMinutes ? { interval_minutes: intervalMinutes } : {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Setup failed: ${res.status}`);
  return data;
}

export async function triggerIndexer(): Promise<{ success: boolean; indexer: string }> {
  const res = await authFetch('/api/custom-domains/indexer/run', { method: 'POST' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Trigger failed: ${res.status}`);
  return data;
}

export interface IndexerStatus {
  success: boolean;
  indexer: string;
  status: string;
  last_run_status: string | null;
  last_run_time: string | null;
  docs_processed: number;
  docs_failed: number;
  errors: string[];
  error?: string;
}

export async function getIndexerStatus(): Promise<IndexerStatus> {
  const res = await authFetch('/api/custom-domains/indexer/status');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Status check failed: ${res.status}`);
  return data;
}
